// Grid-Jobs.jsx
import React, { useMemo, useRef } from 'react';
import { Grid, GridColumn, GridToolbar } from '@progress/kendo-react-grid';
import { Button } from '@progress/kendo-react-buttons';
import { Input } from './form/Input';
import { DropDownList } from './form/DropDownList';
import { SvgIcon } from '@progress/kendo-react-common';
import { saveIcon, trashIcon } from '@progress/kendo-svg-icons';
import { orderBy } from '@progress/kendo-data-query';

/* ------------------------------------------------------------------ */
/* Aufwand-Zelle:  Fokus bleibt, Grid-State erst bei Blur aktualisieren */
/* ------------------------------------------------------------------ */
const AufwandCell = React.memo(
  ({ forwardedRef, dataItem, field, dataIndex, dataState, setDataState }) => {
    const [localValue, setLocalValue] = React.useState(dataItem[field] || '');

    /* Sync in global state beim Verlassen der Zelle */
    const syncToGrid = (val) => {
      const copy = [...dataState];
      copy[dataIndex] = { ...copy[dataIndex], [field]: val };
      setDataState(copy);
    };

    React.useImperativeHandle(forwardedRef, () => ({
      getCurrentValue: () => localValue
    }), [localValue]);

    return (
      <td>
        <Input
          value={localValue}
          onChange={(e) => setLocalValue(e.value)}
          onBlur={() => syncToGrid(localValue)}
          style={{ width: '100%' }}
        />
      </td>
    );
  },
  (prev, next) => prev.dataItem[prev.field] === next.dataItem[next.field]
);

const AufwandCellWithRef = React.forwardRef((props, ref) => (
  <AufwandCell {...props} forwardedRef={ref} />
));

/* ------------------------------------------------------------------ */
/* Haupt-Grid                                                         */
/* ------------------------------------------------------------------ */
const GridJobs = React.memo(
  ({
    dataState,
    sort,
    setSort,
    onRowAction,
    descriptionOptions,   // [{Beschreibung, Einheit}, …]
    setDataState
  }) => {
    /* Refs auf Aufwand-Zellen, um aktuellen Wert bei Save abzuholen */
    const cellRefs = useRef(new Map());
    const getCellRef = (idx) => {
      if (!cellRefs.current.has(idx)) cellRefs.current.set(idx, React.createRef());
      return cellRefs.current.get(idx);
    };

    /* Dropdown-Daten & Mapping */
    const ddlData = useMemo(() => descriptionOptions.map((o) => o.Beschreibung), [descriptionOptions]);
    const unitMap = useMemo(
      () =>
        descriptionOptions.reduce((acc, t) => {
          acc[t.Beschreibung] = t.Einheit;
          return acc;
        }, {}),
      [descriptionOptions]
    );

    const handleDescriptionChange = (desc, rowIdx) => {
      const copy = [...dataState];
      copy[rowIdx] = { ...copy[rowIdx], Beschreibung: desc, Einheit: unitMap[desc] || '' };
      setDataState(copy);
    };

    return (
      <Grid
        data={orderBy(dataState, sort)}
        dataItemKey="Position"
        sortable
        sort={sort}
        onSortChange={(e) => setSort(e.sort)}
      >
        <GridToolbar>
          <Button
            themeColor="primary"
            onClick={() => onRowAction({ rowIndex: dataState.length, operation: 'add' })}
          >
            Zeile hinzufügen
          </Button>
        </GridToolbar>

        <GridColumn title="Position" field="Position" width="100px" />

        <GridColumn
          title="Aufwand"
          field="Aufwand"
          width="100px"
          cell={(props) => (
            <AufwandCellWithRef
              {...props}
              ref={getCellRef(props.dataIndex)}
              dataState={dataState}
              setDataState={setDataState}
            />
          )}
        />

        <GridColumn title="Einheit" field="Einheit" width="100px" />

        <GridColumn
          title="Beschreibung"
          field="Beschreibung"
          cell={(props) => (
            <td>
              <DropDownList
                data={ddlData}
                value={props.dataItem.Beschreibung}
                onChange={(e) => handleDescriptionChange(e.value, props.dataIndex)}
                style={{ width: '100%' }}
              />
            </td>
          )}
        />

        <GridColumn
          title="Befehl"
          width="110px"
          cell={(props) => {
            const rowIndex = props.dataIndex;
            return (
              <td className="k-command-cell">
                <Button
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() =>
                    onRowAction({
                      rowIndex,
                      operation: 'save',
                      dataItem: {
                        ...props.dataItem,
                        Aufwand:
                          cellRefs.current.get(rowIndex)?.current?.getCurrentValue() ??
                          props.dataItem.Aufwand
                      }
                    })
                  }
                >
                  <SvgIcon icon={saveIcon} />
                </Button>
                <Button
                  themeColor="base"
                  onClick={() => onRowAction({ rowIndex, operation: 'remove', dataItem: props.dataItem })}
                >
                  <SvgIcon icon={trashIcon} />
                </Button>
              </td>
            );
          }}
        />
      </Grid>
    );
  }
);

export default GridJobs;
